/**
 * Circuit Breaker for Supabase
 * Prevents cascading failures and provides graceful degradation
 */

class SupabaseCircuitBreaker {
  private failures = 0;
  private state: "closed" | "open" | "half-open" = "closed";
  private lastFailureTime: number = 0;
  private readonly failureThreshold = 3;
  private readonly timeout = 30000; // 30 seconds
  private readonly successThreshold = 1;

  async execute<T>(operation: () => Promise<T>, fallback?: () => T | Promise<T>): Promise<T> {
    // Check if circuit is open
    if (this.state === "open") {
      const now = Date.now();
      if (now - this.lastFailureTime < this.timeout) {
        // Still in timeout period, use fallback or throw
        if (fallback) {
          console.warn("[Circuit Breaker] Circuit open, using fallback");
          return fallback();
        }
        throw new Error("Circuit breaker is open - service unavailable");
      } else {
        // Timeout expired, try half-open
        this.state = "half-open";
        console.log("[Circuit Breaker] Moving to half-open state");
      }
    }

    try {
      const result = await operation();

      // Success - reset failure count
      if (this.state === "half-open") {
        this.failures = 0;
        this.state = "closed";
        console.log("[Circuit Breaker] Circuit closed after successful operation");
      } else {
        this.failures = 0;
      }

      return result;
    } catch (error) {
      this.failures++;
      this.lastFailureTime = Date.now();

      console.error(
        `[Circuit Breaker] Operation failed (${this.failures}/${this.failureThreshold})`,
        error
      );

      if (this.failures >= this.failureThreshold) {
        this.state = "open";
        console.error("[Circuit Breaker] Circuit opened - too many failures");
      }

      // Use fallback if available
      if (fallback) {
        console.warn("[Circuit Breaker] Using fallback due to error");
        return fallback();
      }

      throw error;
    }
  }

  getState(): "closed" | "open" | "half-open" {
    return this.state;
  }

  reset(): void {
    this.failures = 0;
    this.state = "closed";
    this.lastFailureTime = 0;
    console.log("[Circuit Breaker] Circuit reset");
  }
}

// Singleton instance
export const supabaseCircuitBreaker = new SupabaseCircuitBreaker();
